import { ArrowLeftIcon, ArrowRightIcon } from '@radix-ui/react-icons';
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
} from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { PitchDesignHelper } from 'classes/helpers/pitch-design.helper';
import { CommonCallout } from 'components/common/callouts';
import { CopyPitchesDialog } from 'components/common/dialogs/copy-pitches';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import {
  CommonTextInput,
  DEFAULT_TEXT_DEBOUNCE_MS,
} from 'components/common/form/text';
import { BallFlightDesigner } from 'components/sections/pitch-design/ball-flight-designer';
import { NoCsvCallout } from 'components/sections/pitch-uploader/no-csv-callout';
import { PITCH_TYPE_FILTER_OPTIONS } from 'enums/filters';
import { t } from 'i18next';
import { PitchType } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { IPitch } from 'lib_ts/interfaces/pitches';
import { first } from 'lodash';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { NavLocationState } from 'routes/nav-state';
import { useCookiesStore } from 'stores/use-cookies-store';
import { usePitchUploaderStore } from 'stores/use-pitch-uploader-store';

const BTN_CLASS = 'text-titlecase';

export const PitchUploadDesigner = () => {
  const location = useLocation();
  const fromListId = (location.state as NavLocationState | null)?.fromListId;

  const pitch_upload_options = useCookiesStore(
    (s) => s.app.pitch_upload_options
  );

  const balls = usePitchUploaderStore((s) => s.balls);
  const validationWarnings = usePitchUploaderStore((s) => s.validationWarnings);
  const activeID = usePitchUploaderStore((s) => s.activeID);

  const validBalls = balls.filter((m) => !m.fatalError);
  const activeIndex = validBalls.findIndex((m) => m._id === activeID);
  const activeBall = validBalls[activeIndex];

  const [dialogSave, setDialogSave] = useState<number>();
  const [selectedPitches, setSelectedPitches] = useState<Partial<IPitch>[]>([]);

  const handleSave = async (mode: 'one' | 'all') => {
    switch (mode) {
      case 'all': {
        if (validationWarnings.length > 0) {
          NotifyHelper.warning({
            message_md: Array.from(validationWarnings)
              .map((err) => `- ${err}`)
              .join('\n'),
          });
          return;
        }

        const incomplete = validBalls.find((p) => !p.details.name);

        if (incomplete) {
          NotifyHelper.error({
            message_md:
              'Each pitch must have a name. Please check your inputs and try again.',
          });

          usePitchUploaderStore.setState({ activeID: incomplete._id });
          return;
        }

        const validPitches = validBalls.map((b) => b.prepForSave());

        if (fromListId) {
          validPitches.forEach((p) => {
            p._parent_id = fromListId;
          });
        }

        setSelectedPitches(validPitches);
        setDialogSave(Date.now());
        return;
      }

      case 'one': {
        if (!activeBall) {
          return;
        }

        const activeValidationWarnings = activeBall.getWarnings()?.validation;

        if (activeValidationWarnings && activeValidationWarnings.length > 0) {
          NotifyHelper.warning({
            message_md: Array.from(activeValidationWarnings)
              .map((err) => `- ${err}`)
              .join('\n'),
          });
          return;
        }

        const validPitch = activeBall.prepForSave();

        if (fromListId) {
          validPitch._parent_id = fromListId;
        }

        setSelectedPitches([validPitch]);
        setDialogSave(Date.now());
        return;
      }

      default: {
        return;
      }
    }
  };

  if (balls.length === 0) {
    return <NoCsvCallout />;
  }

  if (validBalls.length === 0) {
    return <CommonCallout text="pd.no-valid-pitches" />;
  }

  if (!activeBall) {
    return (
      <CommonCallout color={RADIX.COLOR.DANGER} text="pd.invalid-pitch-index" />
    );
  }

  const prevActive = validBalls[activeIndex - 1];
  const nextActive = validBalls[activeIndex + 1];

  const multiple = validBalls.length > 1;

  const activeValidationWarnings = activeBall.getWarnings()?.validation;

  return (
    <ErrorBoundary identifier="PitchUploadDesigner">
      <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
        <Heading size={RADIX.HEADING.SIZE.SM}>{t('pu.review-pitch')}</Heading>

        {/* header */}
        {activeBall && (
          <Flex gap={RADIX.FLEX.GAP.SM}>
            <Box flexGrow="1">
              <CommonTextInput
                key={`name-${activeBall._id}`}
                id="pitch-uploader-name"
                value={activeBall.details.name}
                placeholder="Type in new name"
                debounceMs={DEFAULT_TEXT_DEBOUNCE_MS}
                onChange={(v) => {
                  activeBall.details.name = v ?? '';
                  usePitchUploaderStore.getState().updateBall(activeBall);
                }}
              />
            </Box>
            <Box width="160px">
              <CommonSearchInput
                key={`type-${activeBall._id}`}
                id="pitch-uploader-type"
                name="type"
                options={PITCH_TYPE_FILTER_OPTIONS}
                values={[activeBall.details.type]}
                onChange={(values) => {
                  activeBall.details.type = first(values) as PitchType;
                  usePitchUploaderStore.getState().updateBall(activeBall);
                }}
                skipSort
              />
            </Box>
            <Box width="80px">
              <CommonSelectInput
                key={`pitch-${activeBall._id}`}
                id="pitch-uploader-valid-index"
                name="validBallIndex"
                value={activeBall._id}
                options={validBalls.map((m, i) => {
                  const o: IOption = {
                    label: `${i + 1}/${validBalls.length}`,
                    value: m._id,
                  };
                  return o;
                })}
                onChange={(v) =>
                  usePitchUploaderStore.setState({ activeID: v })
                }
                suppressWarning
                skipSort
              />
            </Box>
            <IconButton
              color={RADIX.COLOR.NEUTRAL}
              variant={RADIX.BUTTON.VARIANT.SECONDARY}
              onClick={() => {
                usePitchUploaderStore.setState({
                  activeID: prevActive?._id ?? null,
                });
              }}
              disabled={!prevActive}
            >
              <ArrowLeftIcon />
            </IconButton>
            <IconButton
              color={RADIX.COLOR.NEUTRAL}
              variant={RADIX.BUTTON.VARIANT.SECONDARY}
              onClick={() => {
                usePitchUploaderStore.setState({
                  activeID: nextActive?._id ?? null,
                });
              }}
              disabled={!nextActive}
            >
              <ArrowRightIcon />
            </IconButton>
          </Flex>
        )}

        {activeBall?.traj && (
          <BallFlightDesigner
            key={activeBall._id}
            traj={activeBall.traj}
            onUpdatePlate={(plate) => {
              activeBall.plate = plate;
              activeBall.rebuild();

              usePitchUploaderStore.getState().updateBall(activeBall);
              usePitchUploaderStore.setState({ animateKey: Date.now() });
            }}
            onUpdateRelease={(pos) => {
              if (!activeBall.bs) {
                return;
              }

              activeBall.bs.px = pos.px;
              activeBall.bs.pz = pos.pz;
              activeBall.rebuild();

              usePitchUploaderStore.getState().updateBall(activeBall);
              usePitchUploaderStore.setState({ animateKey: Date.now() });
            }}
          />
        )}

        <Flex gap={RADIX.FLEX.GAP.SM} justify="end">
          <Box>
            <Button
              className={BTN_CLASS}
              color={RADIX.COLOR.INFO}
              onClick={() => {
                if (
                  activeValidationWarnings &&
                  activeValidationWarnings.length > 0
                ) {
                  NotifyHelper.warning({
                    message_md: Array.from(activeValidationWarnings)
                      .map((err) => `- ${err}`)
                      .join('\n'),
                  });
                  return;
                }

                handleSave('one');
              }}
            >
              {t('pu.save-current')}
            </Button>
          </Box>

          {multiple && (
            <Box>
              <Button
                className={BTN_CLASS}
                color={RADIX.COLOR.INFO}
                onClick={() => {
                  if (validationWarnings.length > 0) {
                    NotifyHelper.warning({
                      message_md: Array.from(validationWarnings)
                        .map((err) => `- ${err}`)
                        .join('\n'),
                    });
                    return;
                  }

                  handleSave('all');
                }}
              >
                {t('pu.save-all')}
              </Button>
            </Box>
          )}
        </Flex>
      </Flex>

      {dialogSave && selectedPitches.length > 0 && (
        <CopyPitchesDialog
          key={dialogSave}
          identifier="BPU-SavePitchesDialog"
          title={
            <Flex gap="2" align="center">
              {t('pd.add-to-pitch-list')}
              <Badge>
                {PitchDesignHelper.getDisplayBuildPriority(
                  pitch_upload_options.priority
                )}
              </Badge>
            </Flex>
          }
          description={t('pu.create-pitches-form-msg').toString()}
          pitches={selectedPitches}
          onCreated={() => {
            usePitchUploaderStore.getState().setDirty(false);
            setDialogSave(undefined);
          }}
          onClose={() => setDialogSave(undefined)}
        />
      )}
    </ErrorBoundary>
  );
};
